import { useEffect } from 'react'
import { logger } from '@/lib/logging'
import { modelMentionsCallEnding } from './format'
import type { SessionRefs, SessionSetters, SessionUiState } from './types'

interface SessionEffectsProps {
  refs: SessionRefs
  state: SessionUiState
  setters: SessionSetters
  stopAllStreams: (cleanupRecording?: boolean) => Promise<void>
}

export function useSessionEffects({ refs, state, setters, stopAllStreams }: SessionEffectsProps) {
  useTranscriptionActivity(refs, state, setters)
  usePromptActivity(refs, state, setters)
  usePipVideoSource(refs, state.isScreenSharing)
  useInactivityWatchdog(refs, state)
  useUnmountCleanup(refs, stopAllStreams)
}

function useTranscriptionActivity(
  refs: SessionRefs,
  state: SessionUiState,
  setters: SessionSetters
) {
  const { setIsCallEnding, setModelActive, setUserActive } = setters

  useEffect(() => {
    if (!state.transcription.model) return

    setModelActive(true)
    refs.lastActivityRef.current = Date.now()
    if (modelMentionsCallEnding(state.transcription.model)) {
      setIsCallEnding(true)
    }

    const timer = setTimeout(() => setModelActive(false), 1000)
    return () => clearTimeout(timer)
  }, [refs.lastActivityRef, setIsCallEnding, setModelActive, state.transcription.model])

  useEffect(() => {
    if (!state.transcription.user) return

    setUserActive(true)
    refs.lastActivityRef.current = Date.now()
    const timer = setTimeout(() => setUserActive(false), 1000)
    return () => clearTimeout(timer)
  }, [refs.lastActivityRef, setUserActive, state.transcription.user])
}

function usePromptActivity(refs: SessionRefs, state: SessionUiState, setters: SessionSetters) {
  const { setHasGreetingReceived, setShowSpeakFirstPrompt, setShowStartCue } = setters

  useEffect(() => {
    if (!state.aiSpeaking || state.hasGreetingReceived) return

    refs.lastActivityRef.current = Date.now()
    setHasGreetingReceived(true)
    setShowStartCue(true)
    const timer = setTimeout(() => setShowStartCue(false), 4000)
    return () => clearTimeout(timer)
  }, [
    refs.lastActivityRef,
    setHasGreetingReceived,
    setShowStartCue,
    state.aiSpeaking,
    state.hasGreetingReceived,
  ])

  useEffect(() => {
    if (state.aiSpeaking) refs.lastActivityRef.current = Date.now()
  }, [refs.lastActivityRef, state.aiSpeaking])

  useEffect(() => {
    const userHasSpoken = state.transcription.user.trim().length > 0

    if (state.isTimerRunning && !userHasSpoken && !state.hasGreetingReceived) {
      setShowSpeakFirstPrompt(true)
      const timer = setTimeout(() => setShowSpeakFirstPrompt(false), 8000)
      return () => clearTimeout(timer)
    }

    setShowSpeakFirstPrompt(false)
  }, [
    setShowSpeakFirstPrompt,
    state.hasGreetingReceived,
    state.isTimerRunning,
    state.transcription.user,
  ])
}

function usePipVideoSource(refs: SessionRefs, isScreenSharing: boolean) {
  useEffect(() => {
    if (!isScreenSharing || !refs.pipVideoRef.current || !refs.mediaStreamRef.current) return

    refs.pipVideoRef.current.srcObject = refs.mediaStreamRef.current
    refs.pipVideoRef.current.play().catch(() => undefined)
  }, [isScreenSharing, refs.mediaStreamRef, refs.pipVideoRef])
}

function useInactivityWatchdog(refs: SessionRefs, state: SessionUiState) {
  useEffect(() => {
    const interval = setInterval(() => {
      if (!state.isTimerRunning || state.showThankYou) return

      const now = Date.now()
      const inactiveForMs = now - refs.lastActivityRef.current
      const sinceLastKeepAlive = now - refs.lastKeepAliveRef.current

      if (inactiveForMs <= 20000 || sinceLastKeepAlive <= 30000 || !refs.serviceRef.current) {
        return
      }

      refs.lastKeepAliveRef.current = now
      try {
        const interviewState = refs.serviceRef.current.getInterviewState()
        if (interviewState.totalQuestions > 0) {
          refs.serviceRef.current.injectStateUpdate('inactivity-watchdog')
        } else {
          refs.serviceRef.current.sendKeepAlive('inactivity-watchdog')
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          logger.error('Failed to send keep-alive nudge', { error: String(err) })
        }
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [refs, state.isTimerRunning, state.showThankYou])
}

function useUnmountCleanup(
  refs: SessionRefs,
  stopAllStreams: (cleanupRecording?: boolean) => Promise<void>
) {
  useEffect(() => {
    const handleBeforeUnload = () => {
      void stopAllStreams(true)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      refs.shouldReconnectRef.current = false
      refs.isIntentionallyDisconnectingRef.current = false
      window.removeEventListener('beforeunload', handleBeforeUnload)
      void stopAllStreams(true)
      if (refs.reconnectTimeoutRef.current) clearTimeout(refs.reconnectTimeoutRef.current)
      refs.reconnectTimeoutRef.current = null
    }
  }, [refs, stopAllStreams])
}
